import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, errorText } from '../api';
import { useSession } from '../auth/session';
import { FirmaNeuSeite } from '../billtoro/FirmaNeuSeite';
import { KontoMenue } from '../billtoro/KontoMenue';
import { KontoSeite } from '../billtoro/KontoSeite';
import { PlanBadge } from '../billtoro/PlanBadge';
import { SteuerbueroSeite } from '../billtoro/SteuerbueroSeite';
import { TarifSeite } from '../billtoro/TarifSeite';
import { TeamSeite } from '../billtoro/TeamSeite';
import type { Bereich, ShellSeite } from '../billtoro/types';
import { UpgradeDialog } from '../billtoro/UpgradeDialog';
import { istRuht, ZugangRuht } from '../billtoro/ZugangRuht';
import { LegalLinks } from '../components/LegalLinks';
import { useLive } from '../lib/live';
import { isBilltoro } from '../produkt';
import type { Me, MeClient } from '../types';
import { AbsenderForm } from './AbsenderForm';
import { Abos } from './Abos';
import { Angebote } from './Angebote';
import { Auftraege } from './Auftraege';
import { Einstellungen } from './Einstellungen';
import { LieferantenList } from './kontakte/LieferantenList';
import { KundenList } from './KundenList';
import { Lager } from './lager/Lager';
import { Leistungen } from './Leistungen';
import { Mahnlauf } from './Mahnlauf';
import { Steuer } from './ausgaben/Steuer';
import { Mehr, MEHR_PFADE } from './Mehr';
import { RechnungForm } from './RechnungForm';
import { RechnungList } from './RechnungList';
import { StundenzettelStapel } from './StundenzettelStapel';
import { MitRecht, nurLesen, ohneRechtText } from './tarifHinweis';
import type { Overview } from './types';
import { Uebersicht } from './Uebersicht';
import { urlId } from './urlId';
import { Zahlungen } from './Zahlungen';
import { serverZeitMerken, UhrChip } from './zeiten/Uhr';
import { Zeiten } from './zeiten/Zeiten';
import './rechnung.css';

const BILLTORO = isBilltoro();

// Zuletzt gewählte Firma auf diesem Gerät, sonst die erste
function gespeicherteFirma(firms: MeClient[]): string | null {
  try {
    const saved = localStorage.getItem('rt-firma');
    if (saved && firms.some((f) => f.id === saved)) return saved;
  } catch {
    // ohne Speicher: erste Firma
  }
  return firms[0]?.id ?? null;
}

function merken(id: string) {
  try {
    localStorage.setItem('rt-firma', id);
  } catch {
    // egal
  }
}

// Firma aus der Adresse (?firma=<id>, Links in E-Mails an die Firma und in der Glocke), wenn sie zu den Firmen gehört
function firmaAusAdresse(search: string, firms: MeClient[]): string | null {
  const id = urlId(new URLSearchParams(search).get('firma'));
  return id && firms.some((f) => f.id === id) ? id : null;
}

// Startwert: zuerst die Firma aus der Adresse (wird gemerkt), sonst die zuletzt gewählte
function startFirma(firms: MeClient[]): string | null {
  const id = firmaAusAdresse(window.location.search, firms);
  if (!id) return gespeicherteFirma(firms);
  merken(id);
  return id;
}

// Rechnungswerkzeug (Sam 12.10.): eigene Seite unter /rechnung. Anmeldung mit dem Elbcons-Login; der Mandant schreibt
// hier Rechnungen an seine eigenen Kunden. Seit 13.10. führt ein Knopf in der Mandanten-App hierher („Zum
// Rechnungsmodul“) und „Zur Buchhaltung“ zurück.
// Billtoro (src/produkt.ts) ist dieselbe Seite als eigenes Produkt: Logo, Tarif statt „Zur Buchhaltung“, Konto-Menü
// statt „Abmelden“ und die Seiten aus src/billtoro, die auch ohne Firma und ohne Übersicht erreichbar sind.
export function RechnungTool({ me }: { me: Me }) {
  const firms = me.clients.filter((c) => c.status === 'active');
  const [firmId, setFirmId] = useState(() => startFirma(firms));
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Billtoro: Team-Mitglied, dessen Zugang ruht (Tarif der Firma ohne Team): ruhige Seite statt Fehler
  const [ruht, setRuht] = useState(false);
  const { logout } = useSession();
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const mehrAktiv = MEHR_PFADE.some((p) => pathname.startsWith(`/rechnung/${p}`));
  // Billtoro: Reiter „Kontakte“ auch bei den Lieferanten
  const kontakteAktiv = BILLTORO && pathname.startsWith('/rechnung/lieferanten');

  const load = useCallback(async () => {
    if (!firmId) return;
    try {
      const d = await api.get<Overview>(`/api/rechnungstool/${firmId}`);
      // Billtoro: Uhr des Servers für die laufende Uhr (Kopf-Chip, Übersicht)
      serverZeitMerken(d.zeiten?.jetzt);
      setData(d);
      setError(null);
      setRuht(false);
    } catch (err) {
      setError(errorText(err));
      setRuht(BILLTORO && istRuht(err));
    }
  }, [firmId]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  // Billtoro: Rechte oder Team geändert (team.ts firmenGeaendert): neu laden, damit Knöpfe zu den Rechten passen.
  // Ein offenes Formular bleibt offen (MitRecht prüft das Recht nur beim Öffnen), beim Absenden antwortet der Server.
  useLive(BILLTORO ? ['clients'] : null, () => void load());

  // Billtoro: Firmen kommen dazu oder fallen weg, während die Seite offen ist (E-Mail bestätigt, „Weitere Firma“,
  // Firma gelöscht). Dann eine gültige Firma wählen, statt bis zum Neuladen „keine Firma“ zu zeigen.
  const firmIds = firms.map((f) => f.id).join(',');
  useEffect(() => {
    if (!BILLTORO || (firmId && firmIds.split(',').includes(firmId))) return;
    setFirmId(gespeicherteFirma(firms));
    // firms ändert sich mit firmIds
  }, [firmIds, firmId]);

  useEffect(() => {
    if (BILLTORO) document.title = 'Billtoro';
  }, []);

  function waehleFirma(id: string) {
    setFirmId(id);
    merken(id);
  }

  // Link mit ?firma=<id>, während die Seite schon offen ist: zu dieser Firma wechseln
  const adressFirma = firmaAusAdresse(search, firms);
  useEffect(() => {
    if (adressFirma && adressFirma !== firmId) waehleFirma(adressFirma);
    // nur wenn sich die Adresse ändert, nicht bei jeder Wahl im Menü
  }, [adressFirma]);

  function pickFirm(id: string) {
    waehleFirma(id);
    // Adresse der vorigen Firma verwerfen (?vorlage=, ?entwurf=, ?angebot=, /kunden/<id> …), damit kein Formular mit
    // ihren Daten vorbefüllt wird: zurück auf den Bereich
    const bereich = pathname.split('/')[2];
    navigate(bereich ? `/rechnung/${bereich}` : '/rechnung', { replace: true });
  }

  // Inhalt unter der Kopfzeile: ohne Firma ein Hinweis (Billtoro-Steuerbüro ohne eigene Firma: zu den Mandanten), sonst
  // die Bereiche des Rechnungswerkzeugs
  const inhalt = !firmId ? (
    BILLTORO ? (
      me.buero ? (
        <Navigate to="/mandanten" replace />
      ) : (
        <div className="notice info">
          Für Ihr Konto ist noch keine Firma angelegt.{' '}
          <Link to="/rechnung/firma-neu">Firma anlegen</Link>
        </div>
      )
    ) : (
      <div className="notice info">Ihre Firma ist noch nicht freigeschaltet. Danach können Sie hier Rechnungen schreiben.</div>
    )
  ) : error ? (
    ruht ? (
      <ZugangRuht me={me} firmId={firmId} text={error} onFirma={pickFirm} />
    ) : (
      <div className="notice bad">{error}</div>
    )
  ) : !data ? (
    <div className="spinner" aria-label="Lädt" />
  ) : (
    <Routes>
      <Route path="angaben" element={<AbsenderPage data={data} reload={load} />} />
      {data.absender.saved ? (
        <>
          <Route index element={<Uebersicht data={data} reload={load} />} />
          <Route path="rechnungen" element={<RechnungList data={data} reload={load} />} />
          <Route
            path="neu"
            element={
              <MitRecht tarif={data.tarif} bereich="rechnungen" zu="/rechnung/rechnungen">
                <RechnungForm data={data} reload={load} />
              </MitRecht>
            }
          />
          <Route
            path="stundenzettel"
            element={
              <MitRecht tarif={data.tarif} bereich="rechnungen" zu="/rechnung/rechnungen">
                <StundenzettelStapel key={data.firm.id} data={data} />
              </MitRecht>
            }
          />
          <Route path="zahlungen" element={<Zahlungen data={data} reload={load} />} />
          <Route path="kunden/*" element={<KundenList data={data} reload={load} />} />
          {BILLTORO && <Route path="lieferanten/*" element={<LieferantenList data={data} />} />}
          <Route path="angebote/*" element={<Angebote data={data} reload={load} />} />
          <Route path="auftraege/*" element={<Auftraege data={data} reload={load} />} />
          <Route path="leistungen" element={<Leistungen data={data} reload={load} />} />
          <Route path="wiederkehrend/*" element={<Abos data={data} reload={load} />} />
          <Route path="mahnlauf" element={<Mahnlauf data={data} reload={load} />} />
          {BILLTORO && <Route path="zeiten" element={<Zeiten data={data} reload={load} meId={me.user.id} />} />}
          {BILLTORO && <Route path="lager/*" element={<Lager data={data} />} />}
          <Route path="einstellungen" element={<Einstellungen data={data} reload={load} />} />
          <Route path="mehr" element={<Mehr tarif={data.tarif} />} />
          {BILLTORO && <Route path="steuer/*" element={<Steuer data={data} />} />}
          <Route path="*" element={<Navigate to="/rechnung" replace />} />
        </>
      ) : (
        <Route path="*" element={<Navigate to="/rechnung/angaben" replace />} />
      )}
    </Routes>
  );
  const seite: ShellSeite = { me, firmId, data, reload: load, waehleFirma };
  // Billtoro: Formular ohne das Recht geöffnet (tarifHinweis.tsx OhneRecht): Hinweis oben auf der Liste
  const ohneRecht = (state as { ohneRecht?: Bereich } | null)?.ohneRecht;

  return (
    <div className="rt">
      <header className="rt-bar">
        <div className="rt-bar-in">
          <Link to="/rechnung" className="rt-logo" aria-label={BILLTORO ? 'Billtoro' : 'Elbcons Rechnungen'} />
          {!BILLTORO && <span className="rt-title">Rechnungen</span>}
          {firms.length > 1 && (
            <select className="rt-firm" value={firmId ?? ''} onChange={(e) => pickFirm(e.target.value)} aria-label="Firma">
              {firms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          {BILLTORO ? (
            <>
              <UhrChip zeiten={data?.zeiten} />
              <PlanBadge tarif={data?.tarif} />
              <KontoMenue me={me} tarif={data?.tarif} ruht={ruht} />
            </>
          ) : (
            <>
              <Link to="/app" className="rt-app">
                Zur Buchhaltung
              </Link>
              <button type="button" className="rt-out" onClick={() => void logout()}>
                Abmelden
              </button>
            </>
          )}
        </div>
        {data?.absender.saved && (
          <nav className="rt-tabs" aria-label="Bereiche">
            <NavLink to="/rechnung" end>
              Übersicht
            </NavLink>
            <NavLink to="/rechnung/rechnungen">Rechnungen</NavLink>
            <NavLink to="/rechnung/angebote">Angebote</NavLink>
            <NavLink to="/rechnung/auftraege">Aufträge</NavLink>
            <NavLink to="/rechnung/kunden" className={kontakteAktiv ? 'active' : undefined}>
              {BILLTORO ? 'Kontakte' : 'Kunden'}
            </NavLink>
            <NavLink to="/rechnung/zahlungen">
              Zahlungen<span className="rt-tl"> abgleichen</span>
            </NavLink>
            {BILLTORO && <NavLink to="/rechnung/steuer">Ausgaben &amp; Steuer</NavLink>}
            <NavLink to="/rechnung/mehr" className={mehrAktiv ? 'active' : undefined}>
              Mehr
            </NavLink>
          </nav>
        )}
      </header>
      <main className="rt-main">
        {ohneRecht && (
          <p className="notice info" role="status">
            {ohneRechtText(ohneRecht)}
          </p>
        )}
        {BILLTORO ? <BilltoroSeiten seite={seite} inhalt={inhalt} /> : inhalt}
      </main>
      {BILLTORO && <UpgradeDialog firmId={firmId} tarif={data?.tarif} />}
    </div>
  );
}

// Billtoro: Seiten aus src/billtoro vor dem Rest, damit sie ohne Firma (Konto, Firma anlegen), vor „Meine Angaben“
// und auch dann gehen, wenn die Übersicht nicht lädt (Tarif und Konto immer erreichbar). Unten die rechtlichen Seiten.
function BilltoroSeiten({ seite, inhalt }: { seite: ShellSeite; inhalt: ReactNode }) {
  return (
    <>
      <Routes>
        <Route path="konto" element={<KontoSeite {...seite} />} />
        <Route path="firma-neu" element={<FirmaNeuSeite {...seite} />} />
        {seite.firmId && (
          <>
            <Route path="tarif" element={<TarifSeite {...seite} />} />
            <Route path="team" element={<TeamSeite {...seite} />} />
            <Route path="steuerbuero" element={<SteuerbueroSeite {...seite} />} />
          </>
        )}
        <Route path="*" element={inhalt} />
      </Routes>
      <LegalLinks mitAgb />
    </>
  );
}

function AbsenderPage({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const navigate = useNavigate();
  return (
    <AbsenderForm
      firmId={data.firm.id}
      initial={data.absender}
      today={data.today}
      nurLesen={nurLesen(data.tarif)}
      onSaved={async () => {
        await reload();
        navigate('/rechnung');
      }}
    />
  );
}
